import React, { Component } from 'react'
import { BsJournals } from 'react-icons/bs'
import { AiOutlineSearch, AiOutlineWarning } from 'react-icons/ai'
import ModalOrders from '../components/ModalComponent'
import { getInformationWithDataV3 } from '../services/CABE'
import { getValueCookie } from '../services/cookieService'
import { getIndexElement } from '../functions/searchInObject'
import { NumberFormat } from '../functions/generalFunctions'
import { pricesFormat } from '../functions/pricesFormat.js'
import { buildInvoiceCartLine } from './buildInvoiceCartLine'
import '../css/general-style.css'

/**
 * CatalogueV3Invoice — product browser for InvoiceV3 (2026-08-20). Own copy
 * of SalesOrderV3/CatalogueV3.js, kept as a SEPARATE component on purpose
 * (not shared) — InvoiceV3.js and SalesOrderV3.js can diverge over time
 * (different validation rules, different fields on Products, etc.) and a
 * shared component would force every future SalesOrder-only tweak to be
 * threaded through an adapter contract just to avoid touching Invoice, or
 * risk breaking Invoice by accident. Deliberately kept isolated instead —
 * see SalesOrderV3/CatalogueV3.js for the Sales Order version; the two are
 * allowed to drift.
 *
 * Same v3 catalogue data source as SalesOrderV3/CatalogueV3.js
 * (/v3/salesorder/catalog — RDS-only price from product_pricing, no
 * MAS90/WS call), same paginated grid/search/product-line filter. The only
 * real differences are the fields it reads/writes on the parent:
 *   - Customer lives at Padre.state.Invoice.Customer (not SalesOrder.Customer)
 *   - Cart lines use quantityShipped/totalWeight (not quantityOrdered) — see
 *     buildInvoiceCartLine.js, the Invoice-shaped counterpart of
 *     SalesOrderV3/buildCartLine.js
 *
 * Tiered customer pricing (2026-08-19): passes the Invoice's selected
 * customer to /v3/salesorder/catalog, so `row.standardPrice` is already that
 * customer's tier price (level1/2/3, default level 1).
 */
export default class CatalogueV3Invoice extends Component {
    state = {
        showModal: false,
        loading: false,
        products: [],
        total: 0,
        page: 1,
        pageSize: 30,
        search: '',
        productLines: [],
        selectedLine: '',
    }

    open = () => {
        this.setState({ showModal: true })
        this.fetchProductLines()
        this.fetchCatalog()
    }

    close = () => {
        this.setState({ showModal: false })
    }

    async fetchProductLines() {
        if (this.state.productLines.length > 0) return
        try {
            const resp = await getInformationWithDataV3('/v3/salesorder/product-lines', {})
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ productLines: resp.data })
            }
        } catch (error) {
            console.error('fetchProductLines failed:', error)
        }
    }

    async fetchCatalog() {
        this.setState({ loading: true })
        try {
            const idCompany = getValueCookie('CompanyId')
            // Trimmed defensively (2026-08-20) — InvoiceV3.js's Customer comes
            // from the legacy CustomerList.js (MAS90-backed /customer/getList),
            // unlike SalesOrderV3.js's CustomerNo (from the newer, RDS-based
            // CustomerListV3.js) — if MAS90 ever hands back a fixed-width
            // space-padded CustomerNo, an un-trimmed value here would fail to
            // match customer_price_level on the backend and silently resolve
            // to the wrong tier.
            const customerNo = (this.props.Padre.state.Invoice.Customer.CustomerNo || '').trim()
            const resp = await getInformationWithDataV3('/v3/salesorder/catalog', {
                idCompany,
                customerNo,
                search: this.state.search || undefined,
                productLine: this.state.selectedLine || undefined,
                page: this.state.page,
                pageSize: this.state.pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false })
                return
            }
            const products = resp.data || []
            this.setState({ loading: false, products, total: resp.total || 0 })
        } catch (error) {
            console.error('fetchCatalog failed:', error)
            this.setState({ loading: false })
        }
    }

    cartQuantity(itemCode) {
        const products = this.props.Padre.state.Products
        const index = getIndexElement(products, 'itemCode', itemCode)
        if (index === -1) return 0
        return products[index].Visible === 0 ? 0 : Number(products[index].quantityShipped)
    }

    // itemCodes with a setQuantity currently in flight — Father.state.Products
    // is read synchronously here but setState() only commits async, so two
    // rapid clicks (or a click racing a scan from SearchUPC) on the same
    // item can both read the same stale "not in cart yet" snapshot and both
    // unshift a new line, producing a real duplicate row — same class of bug
    // documented in SalesOrderV3/CatalogueV3.js (order AL64684). This lock
    // makes the second call for the same itemCode a no-op until the first
    // one's setState has committed.
    _inFlightItemCodes = new Set()

    setQuantity(row, quantity) {
        if (this._inFlightItemCodes.has(row.itemCode)) return
        this._inFlightItemCodes.add(row.itemCode)

        const Father = this.props.Padre
        const products = Father.state.Products
        const index = getIndexElement(products, 'itemCode', row.itemCode)
        const qty = Math.max(0, quantity)

        if (index !== -1) {
            const existing = products[index]
            // removeProduct() never actually removes the row — it just sets
            // Visible=0 and leaves it in the array (InvoiceV3.js's own
            // design, not something to change here). Re-adding a previously
            // removed line must rebuild it from the CURRENT catalog row, not
            // reuse whatever price/cost/level was left on the old object —
            // same fix as SalesOrderV3/CatalogueV3.js (2026-08-20): otherwise
            // it silently keeps a stale UnitPrice from before the removal.
            if (existing.Visible === 0) {
                products[index] = buildInvoiceCartLine(row, qty, Father.state.Invoice.Customer.CustomerName)
            } else {
                existing.quantityShipped = qty
                existing.totalCost = qty * Number(existing.UnitPrice)
                existing.totalWeight = qty * Number(existing.ShipWeight)
                existing.Visible = qty > 0 ? 1 : 0
                existing.AffectsInvoiceTotal = qty > 0
            }
        } else if (qty > 0) {
            const line = buildInvoiceCartLine(row, qty, Father.state.Invoice.Customer.CustomerName)
            products.unshift(line)
        } else {
            this._inFlightItemCodes.delete(row.itemCode)
            return // nothing to add, quantity is 0 and item isn't in cart yet
        }

        // setState is async — refreshInputs() reads DOM nodes by id for every
        // row, including the one just added/removed, so it MUST run after
        // React has committed the new row count, not right after setState()
        // is called. Doing it inline (no callback) crashes on
        // "Cannot set properties of null" for the not-yet-rendered row.
        Father.setState({ Products: products }, async () => {
            this._inFlightItemCodes.delete(row.itemCode)
            // 2026-08-27 fix — buildInvoiceCartLine() sets minPrice/maxPrice/
            // allowBelowCost/cost straight from this catalog row, which
            // isn't customer-specific (same root issue this file already
            // worked around for priceLevel above, in render()). Re-running
            // classifyProducts() here re-matches the new/updated line against
            // Father.state.pricingMap — already loaded for the CURRENT
            // customer from /v3/pricing/map — so the min/allowBelowCost
            // floor validation actually applies to catalog-added lines
            // instead of silently keeping whatever the generic catalog row
            // happened to carry. No-ops harmlessly if pricing hasn't loaded
            // yet (classifyProducts() itself guards on pricingLoaded).
            await Father.classifyProducts()
            Father.calculateTotals()
            Father.refreshInputs()
            this.forceUpdate() // re-render this modal's cards with the new quantity
        })
    }

    render() {
        const { showModal, loading, products, total, page, pageSize, search, productLines, selectedLine } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))
        // Standardized pricing (2026-08-20) — read from Padre.state.customerPriceLevel
        // (InvoiceV3.js's loadPricingMap(), sourced from /v3/pricing/map) instead of
        // computing our own from /v3/salesorder/catalog's per-row resolvedPriceLevel —
        // that source was returning a wrong/stuck level for Invoice's customers.
        const priceLevel = this.props.Padre.state.customerPriceLevel || 1

        return (
            <>
                <button hidden={this.props.hidden} disabled={this.props.disabled} type="button" onClick={this.open}
                    className={'btn ' + (this.props.colorButton || 'blueButton') + ' btn-lg'}>
                    <BsJournals /> {this.props.titleButton || 'Open Catalogue'}
                </button>

                <ModalOrders title="Catalogue" show={showModal} close={this.close}>
                    {/* So the user always knows which price tier is being applied for
                        this customer before adding anything. Level 1 is the default
                        for a customer with no tier set. */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                        <span style={{
                            background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE',
                            borderRadius: '999px', padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700,
                        }}>Pricing: Level {priceLevel}</span>
                    </div>
                    <div className="card mb-3">
                        <div className="card-body" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1, minWidth: '220px' }}>
                                <div style={{ position: 'relative' }}>
                                    <AiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: '#94A3B8' }} />
                                    <input type="text" className="form-control" style={{ paddingLeft: '32px' }}
                                        placeholder="Search item code or description..." value={search}
                                        onChange={(e) => this.setState({ search: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.setState({ page: 1 }, () => this.fetchCatalog()) }} />
                                </div>
                            </div>
                            <div>
                                <select className="form-select" value={selectedLine}
                                    onChange={(e) => this.setState({ selectedLine: e.target.value, page: 1 }, () => this.fetchCatalog())}>
                                    <option value="">All product lines</option>
                                    {productLines.map((pl) => (
                                        <option key={pl.ProductLine} value={pl.ProductLine}>{pl.ProductLineName}</option>
                                    ))}
                                </select>
                            </div>
                            <button className="btn blueButton btn-lg" onClick={() => this.setState({ page: 1 }, () => this.fetchCatalog())}>Search</button>
                        </div>
                    </div>

                    {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>Loading...</div>}

                    <div className="row g-2">
                        {!loading && products.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>No products found</div>
                        )}
                        {products.map((row) => {
                            const qty = this.cartQuantity(row.itemCode)
                            const hasPrice = row.standardPrice !== null && row.standardPrice !== undefined
                            return (
                                <div key={row.itemCode} className="col-sm-12 col-md-6 col-lg-4">
                                    <div style={{
                                        background: '#ffffff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px',
                                        marginBottom: '10px', display: 'flex', gap: '12px', alignItems: 'center',
                                    }}>
                                        <img src={row.itemURLImage || '/assets/image-not-available.png'} alt={row.itemCode}
                                            style={{ width: '84px', height: '84px', objectFit: 'contain', borderRadius: '8px', background: '#F8FAFC', flexShrink: 0, border: '1px solid #F1F5F9', padding: '4px' }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                                <span style={{ background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{row.itemCode}</span>
                                                <span style={{ background: 'rgba(16,185,129,0.08)', color: '#065F46', border: '1px solid #A7F3D0', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>On hand: {row.quantity || 0}</span>
                                            </div>
                                            {/* Long description (2026-08-20, client's ask) — from CI_Item, falls back to the short one only when it's not synced yet */}
                                            <p style={{ fontSize: '0.85rem', margin: '0 0 6px', color: '#0F172A' }}>{row.longDescription || row.description}</p>
                                            {hasPrice ? (
                                                <p style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>${NumberFormat(pricesFormat(row.standardPrice))}</p>
                                            ) : (
                                                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#991B1B', display: 'flex', alignItems: 'center', gap: '4px', margin: '0 0 8px' }}>
                                                    <AiOutlineWarning /> No standard price set
                                                </p>
                                            )}
                                            <div className="input-group input-group-sm" style={{ maxWidth: '160px' }}>
                                                <button className="btn grayButton" type="button" onClick={() => this.setQuantity(row, qty - 1)}>-</button>
                                                <input type="number" min="0" className="form-control text-center" value={qty} disabled={!hasPrice}
                                                    onChange={(e) => this.setQuantity(row, Number(e.target.value))} />
                                                {/* No price configured for this company → can't be added. Forces the
                                                    fix at the source (Price Management) instead of an unpriced sale. */}
                                                <button className="btn blueButton" type="button" disabled={!hasPrice} title={!hasPrice ? 'Configure a standard price in Price Management first' : ''}
                                                    onClick={() => this.setQuantity(row, qty + 1)}>+</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                        <div className="btn-group btn-group-lg">
                            <button type="button" disabled={page <= 1} onClick={() => this.setState({ page: page - 1 }, () => this.fetchCatalog())} className="btn blueButton">Prev</button>
                            <span style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#64748B' }}>Page {page} of {totalPages}</span>
                            <button type="button" disabled={page >= totalPages} onClick={() => this.setState({ page: page + 1 }, () => this.fetchCatalog())} className="btn blueButton">Next</button>
                        </div>
                    </div>
                </ModalOrders>
            </>
        )
    }
}
